import React, { useEffect, useState, useCallback } from "react";
import { dendrosApi } from "@/lib/dendrosDemo";
import { HealthGauge } from "@/components/dendros-pulse/HealthGauge";
import { ContactRow } from "@/components/dendros-pulse/ContactRow";
import { AuditLogTimeline } from "@/components/dendros-pulse/AuditLogTimeline";
import { Activity, Zap, ShieldCheck, X, RefreshCw, Sprout, KeyRound, ScrollText, GitBranch } from "lucide-react";

const TABS = [
    { id: "audit", label: "Audit", icon: Activity },
    { id: "heal", label: "Heal", icon: Sprout },
    { id: "log", label: "Audit Log", icon: ScrollText },
    { id: "settings", label: "Settings", icon: KeyRound },
];

export function DendrosSidebar({ accountId, onClose, embedded = false, compact = false }) {
    const [audit, setAudit] = useState(null);
    const [account, setAccount] = useState(null);
    const [candidates, setCandidates] = useState([]);
    const [logs, setLogs] = useState([]);
    const [byok, setByok] = useState([]);
    const [tab, setTab] = useState("audit");
    const [loading, setLoading] = useState(false);
    const [pruningId, setPruningId] = useState(null);
    const [autoPrune, setAutoPrune] = useState(false);
    const [byokInput, setByokInput] = useState({ apollo: "", zoominfo: "" });

    const refreshLogs = useCallback(async () => {
        if (!accountId) return;
        const data = await dendrosApi.auditLog(accountId);
        setLogs(data);
    }, [accountId]);

    const refreshAccount = useCallback(async () => {
        if (!accountId) return;
        const data = await dendrosApi.getAccount(accountId);
        setAccount(data.account);
        setAutoPrune(data.account.auto_prune_enabled);
    }, [accountId]);

    const runSync = useCallback(async () => {
        if (!accountId) return;
        setLoading(true);
        try {
            const result = await dendrosApi.runAudit(accountId);
            setAudit(result);
            const cands = await dendrosApi.findReplacements(accountId);
            setCandidates(cands);
            await refreshLogs();
        } finally {
            setLoading(false);
        }
    }, [accountId, refreshLogs]);

    useEffect(() => {
        refreshAccount();
        refreshLogs();
        dendrosApi.listByok().then(setByok).catch(() => {});
    }, [accountId, refreshAccount, refreshLogs]);

    const handlePrune = async (contact) => {
        setPruningId(contact.id);
        try {
            await dendrosApi.prune(contact.id, "Removed via Dendros sidebar");
            await runSync();
        } finally {
            setPruningId(null);
        }
    };

    const handleInject = async (cand) => {
        await dendrosApi.inject(accountId, cand.id);
        await runSync();
    };

    const handleAutoPrune = async (val) => {
        setAutoPrune(val);
        await dendrosApi.toggleAutoPrune(accountId, val);
        if (val && audit) {
            for (const g of audit.ghosts) {
                await dendrosApi.prune(g.contact.id, "Auto-remove triggered");
            }
            await runSync();
        } else {
            await refreshLogs();
        }
    };

    const handleSaveByok = async (provider) => {
        const key = byokInput[provider];
        if (!key) return;
        await dendrosApi.saveByok(provider, key);
        setByokInput((s) => ({ ...s, [provider]: "" }));
        const updated = await dendrosApi.listByok();
        setByok(updated);
    };

    const widthClass = embedded ? "w-[420px]" : compact ? "w-full" : "w-[420px]";

    return (
        <aside
            className={`${widthClass} h-full dendros-surface flex flex-col text-white relative overflow-hidden ${embedded ? "sidebar-slide-in" : ""}`}
            data-testid="dendros-sidebar"
        >
            <div className="dendros-grain" />

            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 relative z-10">
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-md bg-[#10b981] flex items-center justify-center" aria-hidden>
                        <GitBranch size={16} className="text-black" strokeWidth={2.5} />
                    </div>
                    <div>
                        <div className="font-display font-bold text-sm tracking-tight">DENDROS</div>
                        <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Demo · v1.0</div>
                    </div>
                </div>
                {onClose && !compact && (
                    <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="sidebar-close">
                        <X size={18} />
                    </button>
                )}
            </div>

            {account && (
                <div className="px-5 py-3 border-b border-white/10 relative z-10">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Account</div>
                    <div className="font-display text-lg font-semibold text-white truncate" data-testid="sidebar-account-name">
                        {account.name}
                    </div>
                    <div className="flex items-center gap-3 mt-1 font-mono text-[10px] text-zinc-500">
                        <span>{account.industry}</span>
                        <span>·</span>
                        <span>{account.employees.toLocaleString()} EMP</span>
                        <span>·</span>
                        <span>{account.arr} ARR</span>
                    </div>
                </div>
            )}

            <div className="px-5 py-3 border-b border-white/10 relative z-10">
                <button
                    onClick={runSync}
                    disabled={loading}
                    className="btn-emerald-sm w-full justify-center"
                    data-testid="sync-btn"
                >
                    <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
                    {loading ? "Syncing…" : audit ? "Re-Sync With Dendros" : "Sync With Dendros"}
                </button>
            </div>

            <div className="flex border-b border-white/10 relative z-10">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    const active = tab === t.id;
                    return (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 font-mono text-[10px] uppercase tracking-widest transition-colors ${
                                active ? "text-[#10b981] border-b-2 border-[#10b981]" : "text-zinc-500 hover:text-zinc-300 border-b-2 border-transparent"
                            }`}
                            data-testid={`tab-${t.id}`}
                        >
                            <Icon size={12} />
                            <span className="hidden sm:inline">{t.label}</span>
                        </button>
                    );
                })}
            </div>

            <div className="flex-1 overflow-y-auto dendros-scroll relative z-10">
                {tab === "audit" && (
                    <div className="p-5 space-y-5">
                        {!audit ? (
                            <div className="text-center py-10">
                                <Activity size={28} className="mx-auto text-zinc-700 mb-3" />
                                <div className="font-display text-sm text-zinc-300">Awaiting Sync</div>
                                <div className="font-mono text-[10px] text-zinc-500 mt-1 uppercase tracking-widest">Run audit to see ghosts</div>
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center gap-5">
                                    <HealthGauge score={audit.health_score} size={120} />
                                    <div className="flex-1 space-y-2 font-mono text-xs">
                                        <div className="flex justify-between"><span className="text-zinc-500 uppercase tracking-widest text-[10px]">Total</span><span className="text-white">{audit.total_contacts}</span></div>
                                        <div className="flex justify-between"><span className="text-[#fca5a5] uppercase tracking-widest text-[10px]">Ghosts</span><span className="text-[#fca5a5]" data-testid="ghosts-count">{audit.ghosts.length}</span></div>
                                        <div className="flex justify-between"><span className="text-[#fcd34d] uppercase tracking-widest text-[10px]">Stale</span><span className="text-[#fcd34d]" data-testid="stale-count">{audit.stale.length}</span></div>
                                        <div className="flex justify-between"><span className="text-[#34d399] uppercase tracking-widest text-[10px]">Verified</span><span className="text-[#34d399]">{audit.healthy.length}</span></div>
                                    </div>
                                </div>

                                <div className="dendros-card p-3 flex items-center justify-between">
                                    <div>
                                        <div className="font-body text-sm font-semibold text-white">Auto-Remove</div>
                                        <div className="font-mono text-[10px] text-zinc-500 mt-0.5 uppercase tracking-widest">Tier 2 · Continuous</div>
                                    </div>
                                    <button
                                        onClick={() => handleAutoPrune(!autoPrune)}
                                        role="switch"
                                        aria-checked={autoPrune}
                                        className={`relative w-11 h-6 rounded-full transition-colors ${autoPrune ? "bg-[#10b981]" : "bg-zinc-700"}`}
                                        data-testid="auto-prune-toggle"
                                    >
                                        <span className={`absolute top-0.5 ${autoPrune ? "left-[22px]" : "left-0.5"} w-5 h-5 bg-black rounded-full transition-all`} />
                                    </button>
                                </div>

                                {audit.ghosts.length > 0 && (
                                    <div>
                                        <div className="font-mono text-[10px] uppercase tracking-widest text-[#fca5a5] mb-2 flex items-center gap-2">
                                            <span className="w-1 h-1 rounded-full bg-[#ef4444] dendros-pulse-dot" />
                                            Ghosts · {audit.ghosts.length}
                                        </div>
                                        <div className="space-y-2">
                                            {audit.ghosts.map((f) => (
                                                <ContactRow key={f.contact.id} finding={f} onPrune={handlePrune} isPruning={pruningId === f.contact.id} />
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {audit.stale.length > 0 && (
                                    <div>
                                        <div className="font-mono text-[10px] uppercase tracking-widest text-[#fcd34d] mb-2 flex items-center gap-2">
                                            <span className="w-1 h-1 rounded-full bg-[#f59e0b] dendros-pulse-dot" />
                                            Stale · {audit.stale.length}
                                        </div>
                                        <div className="space-y-2">
                                            {audit.stale.map((f) => (
                                                <ContactRow key={f.contact.id} finding={f} onPrune={handlePrune} isPruning={pruningId === f.contact.id} />
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {audit.healthy.length > 0 && (
                                    <div>
                                        <div className="font-mono text-[10px] uppercase tracking-widest text-[#34d399] mb-2">Verified · {audit.healthy.length}</div>
                                        <div className="space-y-2">
                                            {audit.healthy.map((f) => (
                                                <ContactRow key={f.contact.id} finding={f} />
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {audit.missing_icp_roles.length > 0 && (
                                    <div className="dendros-card p-3 border-l-2 border-l-[#ef4444]">
                                        <div className="font-mono text-[10px] uppercase tracking-widest text-[#fca5a5]">ICP Gap Detected</div>
                                        <div className="font-body text-sm text-white mt-1">Missing: {audit.missing_icp_roles.map((r) => r.replaceAll("_", " ")).join(", ")}</div>
                                        <button onClick={() => setTab("heal")} className="btn-emerald-sm mt-3" data-testid="goto-heal-btn">
                                            Heal Pipeline →
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {tab === "heal" && (
                    <div className="p-5 space-y-4">
                        <div>
                            <div className="font-display text-base font-semibold text-white">Heal Pipeline</div>
                            <div className="font-mono text-[10px] text-zinc-500 mt-1 uppercase tracking-widest">BYOK · Enrichment Providers</div>
                        </div>
                        {!audit || audit.missing_icp_roles.length === 0 ? (
                            <div className="dendros-card p-4 text-center">
                                <Sprout size={24} className="mx-auto text-zinc-600 mb-2" />
                                <div className="font-body text-sm text-zinc-300">No ICP gaps detected.</div>
                                <div className="font-mono text-[10px] text-zinc-500 mt-1 uppercase tracking-widest">Run a sync first</div>
                            </div>
                        ) : candidates.length === 0 ? (
                            <div className="dendros-card p-4 text-center font-body text-sm text-zinc-400">No replacement candidates available.</div>
                        ) : (
                            <div className="space-y-2">
                                {candidates.map((c) => (
                                    <div key={c.id} className="dendros-card p-3" data-testid={`candidate-${c.id}`}>
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <div className="font-body font-semibold text-sm text-white">{c.name}</div>
                                                <div className="font-body text-xs text-zinc-400">{c.title}</div>
                                                <div className="font-mono text-[10px] text-zinc-500 mt-1 uppercase tracking-widest">dendros · {c.confidence}% match</div>
                                            </div>
                                            <span className="font-mono text-[10px] uppercase pill-green px-2 py-[2px]">{c.icp_role.replaceAll("_", " ")}</span>
                                        </div>
                                        <button onClick={() => handleInject(c)} className="btn-emerald-sm mt-3 w-full justify-center" data-testid={`inject-${c.id}`}>
                                            Inject Into Salesforce
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {tab === "log" && (
                    <div className="p-5">
                        <div className="font-display text-base font-semibold text-white mb-1">Audit Log</div>
                        <div className="font-mono text-[10px] text-zinc-500 mb-4 uppercase tracking-widest">SOC 2 · Immutable Trail</div>
                        <AuditLogTimeline entries={logs} />
                    </div>
                )}

                {tab === "settings" && (
                    <div className="p-5 space-y-5">
                        <div>
                            <div className="font-display text-base font-semibold text-white">BYOK Vault</div>
                            <div className="font-mono text-[10px] text-zinc-500 mt-1 uppercase tracking-widest">ISO 27001 · Server-vaulted only</div>
                        </div>

                        {[
                            { key: "apollo", label: "Enrichment Provider 1" },
                            { key: "zoominfo", label: "Enrichment Provider 2" },
                        ].map(({ key: p, label }) => {
                            const existing = byok.find((b) => b.provider === p);
                            return (
                                <div key={p} className="dendros-card p-4">
                                    <div className="flex items-center justify-between">
                                        <div className="font-body font-semibold">{label}</div>
                                        {existing ? (
                                            <span className="font-mono text-[10px] uppercase pill-green px-2 py-[2px]">Vaulted</span>
                                        ) : (
                                            <span className="font-mono text-[10px] uppercase pill-yellow px-2 py-[2px]">Not Set</span>
                                        )}
                                    </div>
                                    {existing && (
                                        <div className="font-mono text-[11px] text-zinc-400 mt-2">{existing.key_masked}</div>
                                    )}
                                    <div className="flex items-center gap-2 mt-3">
                                        <input
                                            type="password"
                                            placeholder="API Key"
                                            value={byokInput[p]}
                                            onChange={(e) => setByokInput((s) => ({ ...s, [p]: e.target.value }))}
                                            className="flex-1 bg-black border border-white/10 px-3 py-2 font-mono text-xs text-white focus:border-[#10b981] focus:ring-1 focus:ring-[#10b981]/50 outline-none rounded-md"
                                            data-testid={`byok-input-${p}`}
                                        />
                                        <button onClick={() => handleSaveByok(p)} className="btn-emerald-sm" data-testid={`byok-save-${p}`}>
                                            Vault
                                        </button>
                                    </div>
                                </div>
                            );
                        })}

                        <div className="pt-4 border-t border-white/10">
                            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Compliance</div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-[#10b981] border border-[#10b981]/30 px-2 py-1 rounded-full">
                                    <ShieldCheck size={11} /> SOC 2
                                </span>
                                <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-[#10b981] border border-[#10b981]/30 px-2 py-1 rounded-full">
                                    <ShieldCheck size={11} /> ISO 27001
                                </span>
                                <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-[#10b981] border border-[#10b981]/30 px-2 py-1 rounded-full">
                                    <Zap size={11} /> Zero-Footprint
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </aside>
    );
}
